import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { usePendingArchives } from "@/hooks/usePendingArchives";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortfall: number;
  archiveAt: string;
};

type WsCounts = Record<string, { items: number; excerpts: number }>;

/**
 * Modal that surfaces when an add-on cancellation drops the user below
 * their current owned-workspace count. They nominate which non-primary
 * workspaces to archive at the add-on's period end.
 */
export const AddonRemovalPicker = ({ open, onOpenChange, shortfall, archiveAt }: Props) => {
  const { user } = useAuth();
  const { workspaces } = useWorkspace();
  const { archives, refresh } = usePendingArchives();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState<WsCounts>({});
  const [submitting, setSubmitting] = useState(false);

  const enqueuedIds = useMemo(
    () => new Set(archives.filter((a) => a.reason === "addon_removed").map((a) => a.workspace_id)),
    [archives],
  );

  const candidates = useMemo(
    () => workspaces.filter((w) => !w.is_primary && !enqueuedIds.has(w.id)),
    [workspaces, enqueuedIds],
  );

  useEffect(() => {
    if (!open || candidates.length === 0) return;
    let cancelled = false;
    void (async () => {
      const next: WsCounts = {};
      await Promise.all(
        candidates.map(async (w) => {
          const [{ count: items }, { count: excerpts }] = await Promise.all([
            supabase.from("library_items").select("id", { count: "exact", head: true }).eq("workspace_id", w.id),
            supabase.from("excerpts").select("id", { count: "exact", head: true }).eq("workspace_id", w.id),
          ]);
          next[w.id] = { items: items ?? 0, excerpts: excerpts ?? 0 };
        }),
      );
      if (!cancelled) setCounts(next);
    })();
    return () => { cancelled = true; };
  }, [open, candidates]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < shortfall) next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!user) return;
    if (selected.size !== shortfall) {
      toast.error(`Select exactly ${shortfall} workspace${shortfall === 1 ? "" : "s"}`);
      return;
    }
    setSubmitting(true);
    const rows = Array.from(selected).map((wsId) => ({
      workspace_id: wsId,
      user_id: user.id,
      archive_at: archiveAt,
      reason: "addon_removed",
    }));
    const { error } = await supabase.from("pending_workspace_archives").insert(rows);
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(`${selected.size} workspace${selected.size === 1 ? "" : "s"} scheduled for archive`);
    setSelected(new Set());
    await refresh();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose workspaces to archive</DialogTitle>
          <DialogDescription>
            Your add-on seat{shortfall === 1 ? "" : "s"} cancel{shortfall === 1 ? "s" : ""} on{" "}
            <strong>{new Date(archiveAt).toLocaleDateString()}</strong>. Pick {shortfall} non-primary workspace
            {shortfall === 1 ? "" : "s"} to archive on that date. Their excerpts and library items will be deleted —
            export anything you want to keep first.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-[50vh] overflow-y-auto rounded-md border border-border">
          {candidates.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              No eligible workspaces. Contact support if this seems wrong.
            </p>
          ) : (
            candidates.map((w) => {
              const isSelected = selected.has(w.id);
              const c = counts[w.id];
              return (
                <Label
                  key={w.id}
                  htmlFor={`pick-${w.id}`}
                  className="flex items-start gap-3 px-3 py-3 hover:bg-muted/40 cursor-pointer border-b border-border last:border-b-0"
                >
                  <Checkbox
                    id={`pick-${w.id}`}
                    checked={isSelected}
                    onCheckedChange={() => toggle(w.id)}
                    disabled={!isSelected && selected.size >= shortfall}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{w.workspace_label || w.display_name || "Workspace"}</p>
                    <p className="text-xs text-muted-foreground">
                      {c ? `${c.items} library items · ${c.excerpts} excerpts` : "Loading…"}
                    </p>
                  </div>
                </Label>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Decide later
          </Button>
          <Button onClick={submit} disabled={submitting || selected.size !== shortfall}>
            {submitting ? "Saving…" : `Schedule ${selected.size}/${shortfall}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
